import { StateCreator } from 'zustand'
import { logger, enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import {
  StaffMember,
  IStaffMemberDetail,
  mapStaffMemberToStaffMemberDetail,
  newStaffMember,
} from '$models'
import { SharedSlice } from '../../../../../slice'
import { StaffMembersSlice } from '../slice'
import { staffMemberService } from '$services'

interface AddStaffMemberState {
  staffMember: StaffMember
}

export interface AddStaffMemberSlice extends AddStaffMemberState {
  /**
   * Reset StaffMember Slice
   */
  resetAddStaffMemberSlice: () => void

  /**
   * Add StaffMember
   * @param staffMember StaffMember to add
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  addStaffMember: (
    staffMember: StaffMember,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>
}

const initialState: AddStaffMemberState = {
  staffMember: newStaffMember,
}

const createAddStaffMemberSlice: StateCreator<
  AddStaffMemberSlice & StaffMembersSlice & SharedSlice,
  [],
  [],
  AddStaffMemberSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetAddStaffMemberSlice: () => set(initialState),

  // State API calls
  addStaffMember: async (
    staffMember: StaffMember,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))
      const addedStaffMember: StaffMember = await staffMemberService.add(
        staffMember
      )
      if (!addedStaffMember) {
        logger.error(`${errorMessage} - addedStaffMember doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      const staffMemberDetail: IStaffMemberDetail | null =
        mapStaffMemberToStaffMemberDetail(addedStaffMember)

      set((state) => ({
        staffMember: addedStaffMember,
        ...(staffMemberDetail && {
          activeStaffMembers: [...state.activeStaffMembers, staffMemberDetail],
        }),
      }))
      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },
})

export default createAddStaffMemberSlice
